import { defineStore } from 'pinia';
import { shouldThrottle } from '@/util/rateLimit';
import apiClient from '@/util/apiClient';
import EGS from '@/util/egs_util.ts';
import { stores } from '@/util/stores.js';
import posthog, { identifyUser, resetPostHog } from '@/composables/usePostHog.js';
import { reportError } from '@/services/errorReporting';
import logger from '@/util/logging/logger.js';
import { getPostHogPersonProperties } from '@/util/posthogPersonProperties';
import { getDefaultRedirectPathForRoles } from '@/router/defaultRoleDestination';
import {
	AUTH_SESSION_CLEARED_EVENT,
	AUTH_SESSION_UPDATED_EVENT,
	broadcastSessionCleared,
	broadcastSessionUpdated,
	clearPersistedAuthArtifacts,
	emitAuthSessionCleared,
	emitAuthSessionUpdated,
	getAuthSyncChannel,
	getCurrentTabId,
	hasPersistedAuthSession,
	parseStoredJson,
	persistAuthSessionSnapshot,
	postAuthSyncMessage,
	readAuthSessionSnapshot,
	type AuthSessionEventDetail,
	type AuthSessionSnapshot,
	type AuthSyncMessage,
	type ImpersonationState,
	type SessionUserInfo
} from '@/util/authSession';

/** Last known payout-setup answer, reused when a refresh skips the finance fetch. */

const AUTH_SYNC_TIMEOUT_MS = 1500;
const AUTH_SYNC_MAX_ATTEMPTS = 3;
const authSyncChannel = getAuthSyncChannel();
const currentTabId = getCurrentTabId();
const pendingSessionRequests = new Map<
	string,
	{
		resolve: (snapshot: AuthSessionSnapshot | null) => void;
		timeoutId: number;
	}
>();

let authInitializationPromise: Promise<void> | null = null;
let syncListenersRegistered = false;

const CLUB_PROFILE_EXEMPT_ROLES = new Set(['User', 'USER', 'Club Administrator', 'CLUB_ADMINISTRATOR', 'Club Scheduler', 'CLUB_SCHEDULER']);
const CLUB_ROLES = new Set(['Club Administrator', 'CLUB_ADMINISTRATOR', 'Club Scheduler', 'CLUB_SCHEDULER']);

function isClubOnlyRoleSet(roles: string[] = []): boolean {
	const userRoles = new Set(Array.isArray(roles) ? roles.filter(Boolean) : []);
	if (!Array.from(userRoles).some((role) => CLUB_ROLES.has(role))) {
		return false;
	}
	return Array.from(userRoles).every((role) => CLUB_PROFILE_EXEMPT_ROLES.has(role));
}

const restoreJsonStorageValue = (key: string, value: string | null) => {
	if (value == null) {
		localStorage.removeItem(key);
		return;
	}
	localStorage.setItem(key, value);
};

interface UserDto {
	userNumber?: number | null;
	firstName?: string | null;
	middleName?: string | null;
	lastName?: string | null;
}

interface AuthStoreState {
	isAuthenticated: boolean;
	authToken: string | null;
	userInfo: SessionUserInfo | null;
	user: SessionUserInfo | null;
	userDTO: UserDto;
	showProfileIncompleteNotice: boolean;
	isImpersonating: boolean;
	impersonatedUser: Record<string, unknown> | null;
	impersonationGroupId: number | null;
	impersonationLogId: number | null;
	originalToken: string | null;
	originalUserInfo: Record<string, unknown> | null;
	showSessionExpiredModal: boolean;
	authStateHydrated: boolean;
}

interface AuthActionResult {
	success: boolean;
	message?: string;
	redirectPath?: string;
	impersonatedUser?: Record<string, unknown> | undefined;
}

interface LoginResponseData {
	token?: string;
	accessToken?: string;
	refreshToken?: string | null;
	user: SessionUserInfo;
}

interface ImpersonationResponseData {
	token?: string;
	impersonatedUser?: Record<string, unknown>;
	groupId?: number | null;
	impersonationLogId?: number | null;
	globalImpersonation?: boolean;
}

function getCustomEventDetail(event: Event): AuthSessionEventDetail | null {
	return event instanceof CustomEvent ? (event as CustomEvent<AuthSessionEventDetail>).detail : null;
}

function buildImpersonationState(patch: AuthStoreState): ImpersonationState | null {
	if (!patch.isImpersonating) {
		return null;
	}

	return {
		isImpersonating: patch.isImpersonating,
		impersonatedUser: patch.impersonatedUser,
		impersonationGroupId: patch.impersonationGroupId,
		impersonationLogId: patch.impersonationLogId,
		originalToken: patch.originalToken,
		originalUserInfo: patch.originalUserInfo
	};
}

function getImpersonatedUserNumber(snapshot: AuthSessionSnapshot | null): number | null {
	const impersonatedUser = snapshot?.impersonationState?.impersonatedUser;
	return typeof impersonatedUser?.['userNumber'] === 'number' ? impersonatedUser['userNumber'] : null;
}

function getSessionSyncIdentity(snapshot: AuthSessionSnapshot | null): string | null {
	if (!snapshot) {
		return null;
	}

	const currentUserNumber = typeof snapshot.userInfo.userNumber === 'number' ? snapshot.userInfo.userNumber : null;
	const impersonatedUserNumber = getImpersonatedUserNumber(snapshot);

	return JSON.stringify({
		currentUserNumber,
		impersonatedUserNumber,
		isImpersonating: Boolean(snapshot.impersonationState?.isImpersonating)
	});
}

function shouldAcceptRemoteSessionUpdate(snapshot: AuthSessionSnapshot): boolean {
	const localSnapshot = readAuthSessionSnapshot();
	if (!localSnapshot) {
		return true;
	}

	return getSessionSyncIdentity(localSnapshot) === getSessionSyncIdentity(snapshot);
}

export const useAuthStore = defineStore('authStore', {
	state: (): AuthStoreState => ({
		isAuthenticated: false,
		authToken: null,
		userInfo: null,
		user: null,
		userDTO: {},
		showProfileIncompleteNotice: false,
		isImpersonating: false,
		impersonatedUser: null,
		impersonationGroupId: null,
		impersonationLogId: null,
		originalToken: null,
		originalUserInfo: null,
		showSessionExpiredModal: false,
		authStateHydrated: false
	}),
	actions: {
		setUser(data: SessionUserInfo | null) {
			const nextUser = data || null;
			this.user = nextUser;
			this.userInfo = nextUser;
			const roles = stores.usersStore.roles.length > 0 ? stores.usersStore.roles : parseStoredJson<string[]>(localStorage.getItem('user_roles'), []);
			if (isClubOnlyRoleSet(roles)) {
				this.showProfileIncompleteNotice = false;
				return;
			}
			if (!EGS.UserInformation.isBasicProfileComplete(nextUser || {})) {
				this.showProfileIncompleteNotice = true;
				return;
			}
			this.refreshProfileCompleteness();
		},
		refreshProfileCompleteness() {
			const token = sessionStorage.getItem('auth_token');
			if (!token) {
				this.showProfileIncompleteNotice = false;
				return;
			}

			let userData = {} as SessionUserInfo;
			try {
				userData = (JSON.parse(sessionStorage.getItem('user_info') || '{}') || {}) as SessionUserInfo;
			} catch (error) {
				logger.warn('refreshProfileCompleteness: invalid user_info JSON in sessionStorage', { error });
				this.showProfileIncompleteNotice = true;
				return;
			}
			const roles = stores.usersStore.roles.length > 0 ? stores.usersStore.roles : parseStoredJson<string[]>(localStorage.getItem('user_roles'), []);
			if (isClubOnlyRoleSet(roles)) {
				this.showProfileIncompleteNotice = false;
				return;
			}

			this.showProfileIncompleteNotice = !EGS.UserInformation.isBasicProfileComplete(userData);
		},
		isAuthenticatedSession() {
			return hasPersistedAuthSession();
		},
		registerSessionSyncListeners() {
			if (syncListenersRegistered) {
				return;
			}

			syncListenersRegistered = true;

			authSyncChannel?.addEventListener('message', (event: MessageEvent<AuthSyncMessage>) => {
				const message = event.data;
				if (message.sourceTabId === currentTabId) {
					return;
				}

				if (message.type === 'request-session') {
					const snapshot = readAuthSessionSnapshot();
					if (!snapshot) {
						return;
					}

					postAuthSyncMessage({
						type: 'session-response',
						requestId: message.requestId,
						snapshot,
						sourceTabId: currentTabId
					});
					return;
				}

				if (message.type === 'session-response') {
					const pendingRequest = pendingSessionRequests.get(message.requestId);
					if (!pendingRequest) {
						return;
					}

					window.clearTimeout(pendingRequest.timeoutId);
					pendingSessionRequests.delete(message.requestId);
					pendingRequest.resolve(message.snapshot);
					return;
				}

				if (message.type === 'session-updated') {
					if (!shouldAcceptRemoteSessionUpdate(message.snapshot)) {
						return;
					}

					this.applyAuthSnapshot(message.snapshot, {
						persist: true,
						source: 'remote',
						refreshGroups: true
					});
					return;
				}

				clearPersistedAuthArtifacts();
				this.resetAuthState({ resetAnalytics: true });
				stores.usersStore.resetGroupsState();
				stores.clientStore.clients = [];
				stores.clientPortalStore.clearDashboard();
				emitAuthSessionCleared('remote');
			});

			window.addEventListener(AUTH_SESSION_UPDATED_EVENT, (event) => {
				const detail = getCustomEventDetail(event);
				if (detail?.source !== 'local') {
					return;
				}

				const snapshot = readAuthSessionSnapshot();
				if (!snapshot) {
					return;
				}

				this.applyAuthSnapshot(snapshot, {
					persist: false,
					source: 'local',
					refreshGroups: false
				});
			});

			window.addEventListener(AUTH_SESSION_CLEARED_EVENT, (event) => {
				const detail = getCustomEventDetail(event);
				if (detail?.source !== 'local') {
					return;
				}

				this.resetAuthState({ resetAnalytics: true });
				stores.usersStore.resetGroupsState();
				stores.clientStore.clients = [];
				stores.clientPortalStore.clearDashboard();
			});
		},
		resetAuthState(options: { resetAnalytics?: boolean } = {}) {
			this.isAuthenticated = false;
			this.authToken = null;
			this.userInfo = null;
			this.user = null;
			this.userDTO = {};
			this.showProfileIncompleteNotice = false;
			this.isImpersonating = false;
			this.impersonatedUser = null;
			this.impersonationGroupId = null;
			this.impersonationLogId = null;
			this.originalToken = null;
			this.originalUserInfo = null;
			this.showSessionExpiredModal = false;

			if (options.resetAnalytics) {
				try {
					resetPostHog();
				} catch {}
			}
		},
		syncAnalyticsIdentity(userInfo: SessionUserInfo | null) {
			if (typeof userInfo?.userNumber !== 'number' || !Number.isSafeInteger(userInfo.userNumber) || userInfo.userNumber <= 0) {
				return;
			}

			// Identity is kept only so product errors/events are attributed to the
			// correct account. Login and logout themselves are never captured.
			(identifyUser as unknown as (payload: Record<string, unknown>) => void)({
				userNumber: userInfo.userNumber,
				properties: getPostHogPersonProperties(userInfo)
			});
		},
		applyAuthSnapshot(snapshot: AuthSessionSnapshot, options: { persist: boolean; source: 'local' | 'remote'; refreshGroups: boolean }) {
			if (options.persist) {
				persistAuthSessionSnapshot(snapshot);
			}

			const impersonationState = snapshot.impersonationState;
			this.isAuthenticated = true;
			this.authToken = snapshot.authToken;
			this.userInfo = snapshot.userInfo;
			this.userDTO = {
				userNumber: typeof snapshot.userInfo.userNumber === 'number' ? snapshot.userInfo.userNumber : null,
				firstName: typeof snapshot.userInfo.firstName === 'string' ? snapshot.userInfo.firstName : null,
				middleName: typeof snapshot.userInfo.middleName === 'string' ? snapshot.userInfo.middleName : null,
				lastName: typeof snapshot.userInfo.lastName === 'string' ? snapshot.userInfo.lastName : null
			};
			this.isImpersonating = Boolean(impersonationState?.isImpersonating);
			this.impersonatedUser = (impersonationState?.impersonatedUser as Record<string, unknown> | null) ?? null;
			this.impersonationGroupId = typeof impersonationState?.impersonationGroupId === 'number' ? impersonationState.impersonationGroupId : null;
			this.impersonationLogId = typeof impersonationState?.impersonationLogId === 'number' ? impersonationState.impersonationLogId : null;
			this.originalToken = typeof impersonationState?.originalToken === 'string' ? impersonationState.originalToken : null;
			this.originalUserInfo = (impersonationState?.originalUserInfo as Record<string, unknown> | null) ?? null;

			this.setUser(snapshot.userInfo);
			this.syncAnalyticsIdentity(snapshot.userInfo);
			stores.usersStore.hydrateGroupsFromStorage();

			if (options.refreshGroups) {
				void stores.usersStore
					.usersGroups()
					.then(async () => {
						if (stores.usersStore.hasRole(['Club Administrator', 'Club Scheduler'])) {
							await stores.clientPortalStore.fetchDashboard().catch((error: unknown) => {
								logger.warn('Failed to prefetch client dashboard after auth sync', { error });
							});
						}
						this.refreshProfileCompleteness();
					})
					.catch((error: unknown) => {
						logger.warn('Failed to refresh groups after auth sync', { error });
						this.refreshProfileCompleteness();
					});
			}

			if (options.source === 'remote') {
				emitAuthSessionUpdated('remote');
			}
		},
		publishSessionUpdate() {
			emitAuthSessionUpdated('local');
			broadcastSessionUpdated();
		},
		async requestSessionFromOtherTabs() {
			if (!authSyncChannel) {
				return null;
			}

			for (let attempt = 0; attempt < AUTH_SYNC_MAX_ATTEMPTS; attempt += 1) {
				const snapshot = await new Promise<AuthSessionSnapshot | null>((resolve) => {
					const requestId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${currentTabId}-${String(Date.now())}-${String(attempt)}`;
					const timeoutId = window.setTimeout(() => {
						pendingSessionRequests.delete(requestId);
						resolve(null);
					}, AUTH_SYNC_TIMEOUT_MS);

					pendingSessionRequests.set(requestId, { resolve, timeoutId });
					postAuthSyncMessage({
						type: 'request-session',
						requestId,
						sourceTabId: currentTabId
					});
				});

				if (snapshot) {
					return snapshot;
				}
			}

			return null;
		},
		async initializeAuthState() {
			this.registerSessionSyncListeners();

			if (this.authStateHydrated) {
				return;
			}

			if (authInitializationPromise) {
				await authInitializationPromise;
				return;
			}

			authInitializationPromise = (async () => {
				const existingSnapshot = readAuthSessionSnapshot();
				if (existingSnapshot) {
					this.applyAuthSnapshot(existingSnapshot, {
						persist: false,
						source: 'local',
						refreshGroups: true
					});
					return;
				}

				const strayUserInfo = sessionStorage.getItem('user_info');
				if (strayUserInfo) {
					clearPersistedAuthArtifacts();
				}

				const syncedSnapshot = await this.requestSessionFromOtherTabs();
				if (syncedSnapshot) {
					this.applyAuthSnapshot(syncedSnapshot, {
						persist: true,
						source: 'remote',
						refreshGroups: true
					});
				}
			})().finally(() => {
				this.authStateHydrated = true;
				authInitializationPromise = null;
			});

			await authInitializationPromise;
		},
		async ensureInitialized() {
			await this.initializeAuthState();
		},
		async login(user: Record<string, unknown>): Promise<AuthActionResult> {
			if (shouldThrottle('login', 100)) {
				return { success: false, message: 'Too many login attempts. please try again shortly.' };
			}

			try {
				const { data, status } = await apiClient.post<LoginResponseData>('/auth/login', user);
				if (status !== 200) {
					return { success: false, message: 'Login failed' };
				}

				const authToken = data.token || data.accessToken;
				if (!authToken) {
					return { success: false, message: 'Login failed' };
				}

				const snapshot: AuthSessionSnapshot = {
					authToken,
					refreshToken: data.refreshToken ?? null,
					userInfo: data.user,
					impersonationState: null
				};

				this.applyAuthSnapshot(snapshot, {
					persist: true,
					source: 'local',
					refreshGroups: false
				});
				this.publishSessionUpdate();

				const { userNumber, firstName, middleName, lastName } = data.user;
				this.userDTO = {
					userNumber: typeof userNumber === 'number' ? userNumber : null,
					firstName: typeof firstName === 'string' ? firstName : null,
					middleName: typeof middleName === 'string' ? middleName : null,
					lastName: typeof lastName === 'string' ? lastName : null
				};
				return { success: true };
			} catch (error: unknown) {
				return {
					success: false,
					message: (typeof error === 'object' && error && 'response' in error ? (error as { response?: { data?: string } }).response?.data : null) || 'Login failed. Please check your credentials and try again.'
				};
			}
		},
		async forgotPassword(email: Record<string, unknown>) {
			if (shouldThrottle('forgot_password', 100)) {
				posthog.capture('forgot_password', { action: 'forgot_password' });
				return;
			}

			try {
				return await apiClient.post('/auth/forgot-password', email);
			} catch {}
		},
		async changePassword({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) {
			if (shouldThrottle('change_password', 100)) {
				posthog.capture('change_password', { action: 'change_password' });
				return;
			}
			return await apiClient.post<unknown>('/auth/change-password', { currentPassword, newPassword }, { skipErrorRedirect: true });
		},
		async verifyPasswordResetCode(username: string, code: string) {
			if (shouldThrottle('verify_password', 100)) {
				posthog.capture('verify_password', { action: 'verify_password' });
				return;
			}

			try {
				return await apiClient.post('/auth/verify-reset-code', {
					email: username,
					code
				});
			} catch {}
		},
		async resetPassword(request: Record<string, unknown>) {
			if (shouldThrottle('reset_password', 100)) {
				posthog.capture('reset_password', { action: 'reset_password' });
				return;
			}

			try {
				return await apiClient.post('/auth/reset-password', request);
			} catch {}
		},
		async logout() {
			try {
				const response = await apiClient.post('/logout');
				if (response.status === 200) {
					logger.info('Logout succeeded');
				}
			} catch (error) {
				logger.info('Logout request failed; the session may already be closed', { error });
			} finally {
				clearPersistedAuthArtifacts();
				stores.usersStore.resetGroupsState();
				stores.clientStore.clients = [];
				stores.clientPortalStore.clearDashboard();
				broadcastSessionCleared();
				emitAuthSessionCleared('local');
			}
		},
		async startImpersonation(targetUserId: number, groupId: number | null = null): Promise<AuthActionResult> {
			try {
				const { data, status } = await apiClient.post<ImpersonationResponseData>('/auth/impersonate', {
					targetUserId,
					groupId
				});

				if (status !== 200 || !data.token) {
					return { success: false, message: 'Failed to start impersonation' };
				}

				this.originalToken = this.authToken;
				this.originalUserInfo = this.userInfo ? { ...this.userInfo } : null;
				localStorage.setItem('original_user_roles', localStorage.getItem('user_roles') || '');
				restoreJsonStorageValue('original_user_groups', localStorage.getItem('user_groups'));
				restoreJsonStorageValue('original_user_groups_user_number', localStorage.getItem('user_groups_user_number'));

				this.authToken = data.token;
				this.isImpersonating = true;
				this.impersonatedUser = data.impersonatedUser ?? null;
				this.impersonationGroupId = data.groupId ?? null;
				this.impersonationLogId = data.impersonationLogId ?? null;
				localStorage.removeItem('clients');
				localStorage.removeItem('assignor_clients');
				stores.clientStore.clients = [];
				stores.clientPortalStore.clearDashboard();

				const impersonationState = buildImpersonationState(this.$state);
				persistAuthSessionSnapshot({
					authToken: data.token,
					refreshToken: sessionStorage.getItem('refresh_token'),
					userInfo: this.userInfo || {},
					impersonationState
				});

				const groupsResponse = await apiClient.get<Record<string, unknown>[]>('/users/currentUser/groupRoles');
				const impersonatedGroups = groupsResponse.data;
				stores.usersStore.setGroups(impersonatedGroups, (typeof data.impersonatedUser?.['userNumber'] === 'number' ? data.impersonatedUser['userNumber'] : null) ?? targetUserId);
				if (stores.usersStore.hasRole(['Club Administrator', 'Club Scheduler'])) {
					await stores.clientPortalStore.fetchDashboard({ force: true }).catch((error: unknown) => {
						logger.warn('Failed to prefetch client dashboard during impersonation', { error });
					});
				}
				this.publishSessionUpdate();

				posthog.capture('impersonation_started', {
					targetUserId,
					groupId,
					isGlobal: data.globalImpersonation
				});

				return {
					success: true,
					impersonatedUser: data.impersonatedUser,
					redirectPath: getDefaultRedirectPathForRoles(stores.usersStore.roles)
				};
			} catch (error: unknown) {
				logger.error('Impersonation failed', { error });
				reportError(error, {
					source: 'auth',
					domain: 'impersonation',
					operation: 'start_impersonation',
					handled: true,
					http: { method: 'POST', url: '/auth/impersonate' },
					details: { target_user_id: targetUserId, group_id: groupId }
				});
				return {
					success: false,
					message: (typeof error === 'object' && error && 'response' in error ? (error as { response?: { data?: string } }).response?.data : null) || 'Failed to start impersonation'
				};
			}
		},
		async startSignupImpersonation(sessionToken: string, targetUserId: number): Promise<AuthActionResult> {
			try {
				const { data, status } = await apiClient.post<ImpersonationResponseData>(`/auth/impersonate-signup/${sessionToken}`, {
					targetUserId
				});

				if (status !== 200 || !data.token) {
					return { success: false, message: 'Failed to start signup impersonation' };
				}

				this.originalToken = this.authToken;
				this.originalUserInfo = this.userInfo ? { ...this.userInfo } : null;
				localStorage.setItem('original_user_roles', localStorage.getItem('user_roles') || '');
				restoreJsonStorageValue('original_user_groups', localStorage.getItem('user_groups'));
				restoreJsonStorageValue('original_user_groups_user_number', localStorage.getItem('user_groups_user_number'));

				const impersonationState: ImpersonationState = {
					...buildImpersonationState({
						...this.$state,
						authToken: data.token,
						isImpersonating: true,
						impersonatedUser: data.impersonatedUser ?? null,
						impersonationGroupId: null,
						impersonationLogId: data.impersonationLogId ?? null
					}),
					isSignupImpersonation: true,
					signupSessionToken: sessionToken
				};

				this.authToken = data.token;
				this.isImpersonating = true;
				this.impersonatedUser = data.impersonatedUser ?? null;
				this.impersonationGroupId = null;
				this.impersonationLogId = data.impersonationLogId ?? null;
				localStorage.removeItem('clients');
				localStorage.removeItem('assignor_clients');
				stores.clientStore.clients = [];
				stores.clientPortalStore.clearDashboard();

				persistAuthSessionSnapshot({
					authToken: data.token,
					refreshToken: sessionStorage.getItem('refresh_token'),
					userInfo: this.userInfo || {},
					impersonationState
				});
				this.publishSessionUpdate();

				posthog.capture('signup_impersonation_started', {
					sessionToken,
					targetUserId
				});

				return { success: true, impersonatedUser: data.impersonatedUser };
			} catch (error: unknown) {
				logger.error('Signup impersonation failed', { error });
				reportError(error, {
					source: 'auth',
					domain: 'impersonation',
					operation: 'start_signup_impersonation',
					handled: true,
					http: { method: 'POST', url: '/auth/impersonate-signup/:token' },
					details: { target_user_id: targetUserId }
				});
				return {
					success: false,
					message: (typeof error === 'object' && error && 'response' in error ? (error as { response?: { data?: string } }).response?.data : null) || 'Failed to start impersonation'
				};
			}
		},
		async exitImpersonation(): Promise<AuthActionResult> {
			const savedState = parseStoredJson<ImpersonationState | null>(sessionStorage.getItem('impersonation_state'), null);
			const isCurrentlyImpersonating = this.isImpersonating || savedState?.isImpersonating;

			if (!isCurrentlyImpersonating) {
				return { success: false, message: 'Not currently impersonating' };
			}

			try {
				await apiClient.post('/auth/exit-impersonation').catch(() => {});

				const originalToken = this.originalToken || (typeof savedState?.originalToken === 'string' ? savedState.originalToken : null);
				const originalUserInfo = this.originalUserInfo || ((savedState?.originalUserInfo as Record<string, unknown> | null) ?? null);

				if (!originalToken) {
					clearPersistedAuthArtifacts();
					emitAuthSessionCleared('local');
					broadcastSessionCleared();
					return { success: false, message: 'Original session not found. Please log in again.' };
				}

				if (originalUserInfo) {
					sessionStorage.setItem('user_info', JSON.stringify(originalUserInfo));
				}

				sessionStorage.setItem('auth_token', originalToken);
				sessionStorage.removeItem('impersonation_state');

				this.authToken = originalToken;
				this.userInfo = (originalUserInfo as SessionUserInfo | null) ?? this.userInfo;
				this.isImpersonating = false;
				this.impersonatedUser = null;
				this.impersonationGroupId = null;
				this.impersonationLogId = null;
				this.originalToken = null;
				this.originalUserInfo = null;
				this.showSessionExpiredModal = false;

				if (originalUserInfo) {
					this.setUser(originalUserInfo as SessionUserInfo);
				}

				const originalRoles = localStorage.getItem('original_user_roles');
				const originalGroups = localStorage.getItem('original_user_groups');
				const originalGroupsUserNumber = localStorage.getItem('original_user_groups_user_number');

				if (originalRoles) {
					localStorage.setItem('user_roles', originalRoles);
				} else {
					localStorage.removeItem('user_roles');
				}

				if (originalGroups) {
					localStorage.setItem('user_groups', originalGroups);
				} else {
					localStorage.removeItem('user_groups');
				}

				if (originalGroupsUserNumber) {
					localStorage.setItem('user_groups_user_number', originalGroupsUserNumber);
				} else {
					localStorage.removeItem('user_groups_user_number');
				}

				localStorage.removeItem('original_user_roles');
				localStorage.removeItem('original_user_groups');
				localStorage.removeItem('original_user_groups_user_number');
				localStorage.removeItem('clients');
				localStorage.removeItem('assignor_clients');

				stores.usersStore.resetGroupsState();
				stores.clientStore.clients = [];
				stores.clientPortalStore.clearDashboard();

				try {
					await stores.usersStore.usersGroups({ force: true });
				} catch (refreshError) {
					logger.warn('Failed to refresh groups after impersonation exit', { error: refreshError });
					if (originalGroups) {
						stores.usersStore.setGroups(parseStoredJson<Record<string, unknown>[]>(originalGroups, []), originalGroupsUserNumber != null ? Number(originalGroupsUserNumber) : null);
					}
				}

				persistAuthSessionSnapshot({
					authToken: originalToken,
					refreshToken: sessionStorage.getItem('refresh_token'),
					userInfo: (originalUserInfo as SessionUserInfo | null) || this.userInfo || {},
					impersonationState: null
				});
				this.publishSessionUpdate();

				posthog.capture('impersonation_ended');
				return {
					success: true,
					redirectPath: getDefaultRedirectPathForRoles(parseStoredJson<string[]>(localStorage.getItem('user_roles'), []))
				};
			} catch (error) {
				logger.error('Failed to exit impersonation', { error });
				return { success: false, message: 'Failed to exit impersonation' };
			}
		},
		async initiateResetPassword(payload: Record<string, unknown>) {
			try {
				await apiClient.post('/auth/admin-password-reset/initiate', payload);
			} catch (error) {
				logger.error('Failed to initiate password reset', { error });
			}
		},
		handleImpersonationSessionExpired() {
			if (this.isImpersonating) {
				this.showSessionExpiredModal = true;
				posthog.capture('impersonation_session_expired');
			}
		},
		dismissSessionExpiredModal() {
			this.showSessionExpiredModal = false;
		},
		restoreImpersonationState() {
			const savedState = parseStoredJson<ImpersonationState | null>(sessionStorage.getItem('impersonation_state'), null);
			if (!savedState?.isImpersonating) {
				return;
			}

			this.isImpersonating = true;
			this.impersonatedUser = (savedState.impersonatedUser as Record<string, unknown> | null) ?? null;
			this.impersonationGroupId = typeof savedState.impersonationGroupId === 'number' ? savedState.impersonationGroupId : null;
			this.impersonationLogId = typeof savedState.impersonationLogId === 'number' ? savedState.impersonationLogId : null;
			this.originalToken = typeof savedState.originalToken === 'string' ? savedState.originalToken : null;
			this.originalUserInfo = (savedState.originalUserInfo as Record<string, unknown> | null) ?? null;
		},
		async checkImpersonationStatus(): Promise<{ isImpersonating: boolean; impersonatorUserNumber?: number }> {
			try {
				const { data } = await apiClient.get<{ isImpersonating: boolean; impersonatorUserNumber?: number }>('/auth/impersonation-status');
				return data;
			} catch (error: unknown) {
				logger.error('Failed to check impersonation status', { error });
				return { isImpersonating: false };
			}
		}
	}
});
